import {
  Box,
  Button,
  Divider,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
} from "@avenue-8/sidekick-ui";
import {
  KeyFeaturesSectionConfig,
  KeyFeaturesSectionView,
} from "@cma/presentation-generation-logic";
import AddIcon from "@mui/icons-material/Add";
import { useCallback, useState } from "react";
import { KeyFeatureItem } from "./components/KeyFeatureItem";
import { useKeyFeaturesLogic } from "./useKeyFeaturesLogic";
import { KeyFeaturesGrid, KeyFeaturesWrapper } from "./KeyFeatures.styles";
import { PresentationSectionProps } from "../PresentationSectionProps";
import {
  convertKeyFeaturesValidationSchemaToFeaturedProperty,
  EditableKeyFeatures,
} from "./helpers/key-features-form-helpers";

type Props = PresentationSectionProps<
  KeyFeaturesSectionView,
  KeyFeaturesSectionConfig
>;

export const KeyFeaturesEdit = (props: Props) => {
  const { sectionConfig, sectionView, onUpdateSection, sourceData } = props;
  const { id, title } = sectionConfig;
  const isOffMarket =
    !sourceData.featuredProperty.mlsId ||
    !sourceData.featuredProperty.mlsSource;
  const { keyFeaturesOptions } = useKeyFeaturesLogic({
    sectionView,
    hideEmptyFeatures: !isOffMarket,
  });
  const [error, setError] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleAddFeatureClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseKeyFeaturesMenu = () => {
    setAnchorEl(null);
  };

  const handleFeatureSelect = useCallback(
    (featureName: keyof KeyFeaturesSectionConfig) => {
      onUpdateSection({
        sectionConfig: {
          ...sectionConfig,
          [featureName]: true,
        },
      });
      handleCloseKeyFeaturesMenu();
    },
    [onUpdateSection, sectionConfig],
  );

  const handleDeleteFeature = useCallback(
    (featureName: keyof KeyFeaturesSectionConfig) => {
      onUpdateSection({
        sectionConfig: {
          ...sectionConfig,
          [featureName]: false,
        },
      });
    },
    [onUpdateSection, sectionConfig],
  );

  const handleEditFeature = useCallback(
    (valueName: EditableKeyFeatures, newValue: string) => {
      const convertedFeaturedProperty =
        convertKeyFeaturesValidationSchemaToFeaturedProperty({
          [valueName]: newValue,
        });
      onUpdateSection({
        sectionConfig,
        editableSourceData: {
          featuredProperty: {
            ...convertedFeaturedProperty,
          },
        },
      });
    },
    [onUpdateSection, sectionConfig],
  );

  const handleEditFeatureError = useCallback((error: string | null) => {
    setError(error);
  }, []);

  const hiddenFeatures = keyFeaturesOptions.filter(
    (feature) => feature.show === false && (!feature.isEmpty || isOffMarket),
  );

  return (
    <Box id={id} pt={4} pb={6} px={4} textAlign="center">
      <Typography variant="h5">{title}</Typography>

      {/* DESCRIPTION IS TOO LONG */}
      {/* {!!description && (
        <Typography mt={2} maxWidth={540} mx={"auto"}>
          {description}
        </Typography>
      )} */}

      <KeyFeaturesWrapper>
        <KeyFeaturesGrid>
          {keyFeaturesOptions.map((feature) => {
            if (!feature.show) return null;
            return (
              <KeyFeatureItem
                key={feature.configName}
                configName={feature.configName}
                valueName={feature.valueName}
                title={feature.title}
                value={feature.value}
                onDelete={handleDeleteFeature}
                onEdit={handleEditFeature}
                onEditError={handleEditFeatureError}
                editableValue={isOffMarket && !!feature.valueName}
                editMode
              />
            );
          })}
        </KeyFeaturesGrid>

        <Divider sx={{ width: "100%" }} />

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <Tooltip
          title={
            hiddenFeatures.length === 0 ? "No feature available to add" : ""
          }
        >
          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={handleAddFeatureClick}
              disabled={hiddenFeatures.length === 0}
              aria-controls={open ? "add-feature-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              Add Feature
            </Button>
          </Box>
        </Tooltip>

        <Menu
          id="add-feature-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseKeyFeaturesMenu}
          MenuListProps={{
            "aria-labelledby": "add-feature-button",
          }}
        >
          {hiddenFeatures.map((feature) => (
            <MenuItem
              key={feature.configName}
              onClick={() => handleFeatureSelect(feature.configName)}
              sx={{
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "0.125rem",
                minHeight: "48px",
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {feature.title}
              </Typography>
              <Typography variant="body2">{feature.value}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </KeyFeaturesWrapper>
    </Box>
  );
};
